import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useDisconnect } from 'wagmi';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { disconnect } = useDisconnect();
  const previousUserIdRef = useRef(null);

  // Check session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Disconnect wagmi when user changes (logout or different user logs in)
  useEffect(() => {
    if (previousUserIdRef.current !== null && previousUserIdRef.current !== user?.id) {
      // User changed — clear wagmi state
      disconnect();
    }
    previousUserIdRef.current = user?.id ?? null;
  }, [user?.id, disconnect]);

  const checkAuth = async () => {
    try {
      const data = await api.get('/auth/me');
      setUser({
        id: data.customer_id,
        email: data.email,
        name: data.name,
        hasPaymentMethod: data.has_payment_method,
      });
    } catch (error) {
      // Not authenticated
      setUser(null);
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = null, turnstileToken = null, savedVerificationToken = null) => {
    let verificationToken = savedVerificationToken;
    
    // Step 1: If no saved token, call /auth/login first
    if (!verificationToken) {
      const loginData = await api.post('/auth/login', { email, password, turnstile_token: turnstileToken || undefined });
      
      if (loginData.status !== 'verification_required' || !loginData.verification_token) {
        throw new Error('Unexpected login response');
      }
      verificationToken = loginData.verification_token;
    }
    
    // Step 2: Complete login with verification token + optional 2FA code
    const verifyPayload = { 
      verification_token: verificationToken 
    };
    if (totpCode) {
      verifyPayload.totp_code = totpCode;
    }
    
    try {
      const data = await api.post('/auth/verify', verifyPayload);
      
      // Session is set via httpOnly cookie by backend
      setUser({
        id: data.customer_id,
        email: data.email,
        name: data.name,
        hasPaymentMethod: false,
      });
      return data;
    } catch (error) {
      // If verification fails, it might need 2FA code
      // Return a hint to prompt for 2FA (generic - don't reveal if 2FA is enabled)
      if (error.status === 401 && !totpCode) {
        return { 
          requires_2fa: true, 
          verification_token: verificationToken,
          message: 'Please enter your verification code if you have 2FA enabled'
        };
      }
      throw error;
    }
  };

  const signup = async (email, password, name, turnstileToken) => {
    const data = await api.post('/auth/signup', { email, password, name, turnstile_token: turnstileToken || undefined });
    // Session is set via httpOnly cookie by backend
    setUser({
      id: data.customer_id,
      email: data.email,
      name: data.name,
      hasPaymentMethod: false,
    });
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore errors during logout
    }
    localStorage.removeItem('session_token');
    disconnect(); // Clear wagmi wallet state on logout
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
